import { Component, DestroyRef, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { Observe } from '@bo-client/shared/observe.util';
import { Observable } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { combineLatestWith, map, shareReplay, tap } from 'rxjs/operators';

@Component({
  selector: 'app-comparison-table',
  templateUrl: './comparison-table.component.html',
  styleUrl: './comparison-table.component.scss'
})
export class ComparisonTableComponent implements OnInit {
  @Input() headers: ComparisonTableHeader = {
    labelHeader: 'Label',
    firstDataHeader: 'Erster Datenpunkt',
    secondDataHeader: 'Zweiter Datenpunkt'
  };
  @Input() data: ComparisonTableData[];
  @Input() ignoreIfSecondIsNull: boolean = false;
  @Observe('headers') headers$: Observable<ComparisonTableHeader>;
  @Observe('data') data$: Observable<ComparisonTableData[]>;
  @Observe('ignoreIfSecondIsNull') ignoreIfSecondIsNull$: Observable<boolean>;

  @Output() fieldMismatch = new EventEmitter<string[]>();

  private readonly destroyRef = inject(DestroyRef);

  comparedData$: Observable<(ComparisonTableData & { isEqual: boolean; isInDeviationRange: boolean })[]>;

  ngOnInit(): void {
    this.comparedData$ = this.data$.pipe(
      takeUntilDestroyed(this.destroyRef),
      combineLatestWith(this.ignoreIfSecondIsNull$),
      map(([dataSet, ignoreIfSecondIsNull]) => {
        return (dataSet || []).map((data) => {
          let isEqual: boolean = false;
          let isInDeviationRange: boolean = false;
          if (ignoreIfSecondIsNull && !isNotNullOrUndefined(data.secondDataPoint)) {
            isEqual = true;
          } else if (
            !isNotNullOrUndefined(data.firstDataPoint) &&
            !isNotNullOrUndefined(data.secondDataPoint)
          ) {
            // Wenn beide Datenpunkte nicht gesetzt sind, zählen sie als gleich
            isEqual = true;
          } else if (
            isNotNullOrUndefined(data.firstDataPoint) &&
            isNotNullOrUndefined(data.secondDataPoint)
          ) {
            if (data.firstDataPoint.toString() === data.secondDataPoint.toString()) {
              isEqual = true;
            } else if (data.allowedDeviation) {
              isInDeviationRange = this.checkDeviation(data);
            }
          }
          return { ...data, isEqual, isInDeviationRange };
        });
      }),
      tap((data) => {
        const mismatchedFields = data
          .filter((d) => !d.warnOnly && !d.isEqual && !d.isInDeviationRange && !!d.label)
          .map((d) => d.label);
        this.fieldMismatch.emit(mismatchedFields);
      }),
      shareReplay(1)
    );
  }

  private checkDeviation = (data: ComparisonTableData): boolean => {
    const firstNumber = Number(data.firstDataPoint);
    const secondNumber = Number(data.secondDataPoint);
    if (isNaN(firstNumber) && isNaN(secondNumber)) {
      return false;
    }
    const deviation = Math.abs(firstNumber - secondNumber);
    return deviation <= Math.abs(data.allowedDeviation);
  };
}

export interface ComparisonTableHeader {
  labelHeader: string;
  firstDataHeader: string;
  secondDataHeader: string;
}
export interface ComparisonTableData {
  label: string;
  firstDataPoint: unknown;
  secondDataPoint: unknown;
  allowedDeviation?: number;
  warnOnly?: boolean;
}

const isNotNullOrUndefined = (value: unknown) => {
  return value !== null && value !== undefined;
};
